package com.narbase.letsgo.web.utils.views.filters


import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.weightOf
import com.narbase.kunafa.core.dimensions.dimen
import com.narbase.kunafa.core.dimensions.percent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.drawable.Color
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.common.AppFontSizes
import com.narbase.letsgo.web.network.makeNotVisible
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.PopupZIndex
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.views.MaterialIcon
import com.narbase.letsgo.web.utils.views.customViews.deleteButton
import com.narbase.letsgo.web.utils.views.matchParentWidth
import com.narbase.letsgo.web.utils.views.materialIcon
import com.narbase.letsgo.web.utils.views.pointerCursor
import disableBlurOptions
import tippy
import kotlin.js.Json
import kotlin.js.json

class FiltersView(
    private val filters: List<FilterType<*>>,
) : Component(), FilterParent {
    private var filtersViewWidth: Int = 320
    private var tippyInstance: dynamic = null
    private var selectedFiltersListView: LinearLayout? = null
    private var iconView: MaterialIcon? = null
    private var buttonLayout: View? = null

    init {
        filters.forEach { it.filterParent = this }
    }

    abstract class FilterType<FilterValue> : Component() {
        lateinit var filterParent: FilterParent

        abstract fun onFilterCleared()
        abstract fun getFilterValueAsString(): String?

    }

    override fun onViewCreated(lifecycleOwner: LifecycleOwner) {
        super.onViewCreated(lifecycleOwner)
        theme.createTippyCustomStyles()
    }

    fun dismiss() {
        tippyInstance?.hide()
    }

    private val filtersList by lazy {
        detached.verticalLayout {
            style {
                minWidth = filtersViewWidth.px
                padding = 16.px
                borderRadius = 8.px
                backgroundColor = Color.white
            }

            horizontalLayout {
                val filtersDivided = filters.chunked(4)
                filtersDivided.forEachIndexed { index, chunk ->
                    verticalLayout {
                        chunk.forEach { filter ->
                            verticalLayout {
                                style {
                                    minWidth = 100.percent
                                }
                                mount(filter)
                            }
                            verticalFiller(theme.standardSpacing)
                        }
                    }
                    if (index != filtersDivided.size - 1) {
                        horizontalFiller(theme.standardSpacing)
                    }
                }
            }

            verticalFiller(4)
            theme.unimportantButton(this) {
                matchParentWidth()
                text = "Dismiss".localized()
                onClick = {
                    dismiss()
                }
            }
        }
    }

    private fun LinearLayout.selectedFilter(filterValue: String, onDelete: () -> Unit): View {
        return horizontalLayout {
            style {
                alignItems = Alignment.Center
                border = "1px solid ${AppColors.borderColor}"
                borderRadius = 4.px
                padding = "4px 12px".dimen()
                marginEnd = 16.px
                position = "relative"
                backgroundColor = AppColors.extraLightBackground
            }
            deleteButton(onDeleteClick = {
                onDelete()
                makeNotVisible(this)
            })

            textView {
                style {
                    fontSize = AppFontSizes.smallerText
                }
                text = filterValue
            }
        }
    }

    override fun onFilterUpdated() {
        updateSelectedFiltersListView()
    }

    private fun updateSelectedFiltersListView() {
        selectedFiltersListView?.clearAllChildren()
        selectedFiltersListView?.apply {
            filters.forEach { filterType ->
                val filterValue = filterType.getFilterValueAsString()
                if (filterValue != null) {
                    selectedFilter(filterValue, onDelete = {
                        filterType.onFilterCleared()
                    })
                }
            }
        }
    }


    private val tippyOptions: Json
        get() = json(
            "interactive" to true,
            "trigger" to "click",
            "theme" to "popover",
            "placement" to "bottom-start",
            "arrow" to "large",
            "zIndex" to PopupZIndex.getTopIndex(),
            "content" to filtersList.element,
//            "followCursor" to "initial",
            "maxWidth" to "none",
            "hideOnClick" to false,
            "onShow" to { onShown() },
            "onHide" to { onHidden() },
            disableBlurOptions,
        )

    private fun onShown() {
        iconView?.element?.innerText = "close"
        buttonLayout?.element?.style?.backgroundColor = AppColors.buttonHighlightedColor.toCss()
        iconView?.onClick = {
            dismiss()
            it.stopPropagation()
        }
    }

    private fun onHidden() {
        iconView?.element?.innerText = "filter_list"
        buttonLayout?.element?.style?.backgroundColor = AppColors.white.toCss()
        iconView?.onClick = {}
    }

    override fun View?.getView(): View = horizontalLayout {
        style {
            width = matchParent
        }
        buttonLayout = horizontalLayout {
            style {
                padding = "4px 12px".dimen()
                alignItems = Alignment.Center
                borderRadius = 4.px
                border = "1px solid ${AppColors.separatorLight}"
                pointerCursor()
                fontSize = 14.px
            }
            iconView = materialIcon("filter_list") {
                style {
                    fontSize = AppFontSizes.smallText
                    marginEnd = 8.px
                }
            }
            textView {
                id = "filterByButton"

                style {
                    fontSize = AppFontSizes.smallText
                    color = AppColors.text
                }
                text = "Filter by".localized()
            }

            tippyInstance = tippy(this.element, tippyOptions)
        }

        selectedFiltersListView = horizontalLayout {
            style {
                width = weightOf(1)
                marginStart = 8.px
            }
        }
    }

    private fun createTippyCustomStyles() {
        try {
            stringRuleSet(".tippy-tooltip.filter-theme") {
                borderRadius = 12.px
                backgroundColor = Color.white
                color = Color.black
                boxShadow = "0 3px 14px -0.5px rgba(0,8,16,.08)"
                border = " 1px solid ${AppColors.borderColorHex}"
            }
            stringRuleSet(".tippy-tooltip.filter-theme .tippy-backdrop") {
                backgroundColor = Color.white
            }
        } catch (e: dynamic) {
            console.log(e)
        }
    }

    override fun onViewRemoved(lifecycleOwner: LifecycleOwner) {
        super.onViewRemoved(lifecycleOwner)
        dismiss()
    }
}

interface FilterParent {
    /**
     * Should be called when the filter is updated
     */
    fun onFilterUpdated()
}
